import styled, { css } from 'styled-components'
import { Button, Radio, Checkbox, Icon, Input } from 'antd'

import { styleguide } from '../../../../../constants'

const { colors } = styleguide
const readableText = '#353c49'
const neutralCard = '#f8faff'
const neutralHover = '#f2f6ff'
const neutralSelected = '#eaf1ff'
const surface = '#ffffff'
const edge = '#e6eaf0'

const Quiz = styled.div`
  padding: 1.1rem;
  overflow-y: auto;
  background: ${surface};
  border-radius: 12px;
  box-shadow: inset 0 0 0 1px ${edge}, 0 1px 4px rgba(17, 25, 38, 0.04);
  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 10px;
  }
  ::-webkit-scrollbar-track {
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    background-color: #fff;
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.5);
    -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  }
`

Quiz.Content = styled.div`
  margin-top: 0.6rem;
  padding: 0.35rem 0.2rem 0.2rem;
  background: transparent;
`

Quiz.Action = styled.div`
  margin-top: 0.9rem;
  padding: 0.55rem 0.2rem 0.1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.65rem;

  .ant-alert {
    width: 100%;
    margin: 0;
  }
`

Quiz.Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.2rem;
  padding: 0.25rem 0.2rem;
`

Quiz.Title = styled.div`
  font-weight: 500;
  font-size: 1.4rem;
  margin-bottom: 0;
  color: #5d6677;
  flex: 1 1 auto;
  min-width: 0;
  line-height: 1.2;
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.45rem;
`

Quiz.TitleCount = styled.span`
  min-width: 2.2rem;
  height: 2.2rem;
  padding: 0 0.7rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  font-weight: 700;
  color: #1e2430;
  background: #f1f4f8;
  box-shadow: inset 0 0 0 1px #e1e6ee;
`

Quiz.TitleWord = styled.span`
  letter-spacing: 0.01em;
`

Quiz.NextButton = styled(Button)`
  && {
    border-radius: 999px;
    height: 3.5rem;
    padding: 0 1.3rem;
    font-size: 1.3rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    white-space: nowrap;
    border: 0;
    background: linear-gradient(135deg, #4f7cff 0%, #3f69e0 100%);
    box-shadow: 0 4px 10px rgba(63, 105, 224, 0.28);

    &:hover,
    &:focus {
      background: linear-gradient(135deg, #5a85ff 0%, #466fe6 100%);
      box-shadow: 0 5px 12px rgba(63, 105, 224, 0.3);
    }

    &[disabled] {
      background: #d6dcec;
      box-shadow: none;
      color: rgba(17, 25, 38, 0.45);
    }
  }
`

Quiz.Strong = styled.span`
  font-weight: 700;
`

Quiz.Question = styled.div`
  font-weight: 600;
  font-size: 1.75rem;
  margin-bottom: 0.8rem;
  padding: 0.2rem 0.2rem 0.6rem;
  color: #1f2532;
  line-height: 1.4;
  border-bottom: 1px solid #eceff3;
`

Quiz.Radio = styled(Radio)`
  && {
    width: 100%;
    min-height: 3.2rem;
    line-height: 1.5;
    font-size: 1.5rem;
    font-weight: 400;
    color: ${readableText};
    display: flex;
    align-items: flex-start;
    gap: 0.8rem;
    margin-bottom: 0.52rem;
    padding: 0.72rem 0.92rem;
    border: 0;
    border-radius: 10px;
    background: ${neutralCard};
    box-shadow: inset 0 0 0 1px #dfe8fb;
    transition: background-color 0.15s ease, box-shadow 0.15s ease;
    white-space: initial;
    text-align: left;

    .ant-radio {
      top: 0.2rem;
    }

    .ant-radio + span {
      color: ${readableText};
      line-height: 1.5;
      padding-left: 0;
    }

    &:hover {
      background: ${neutralHover};
      box-shadow: inset 0 0 0 1px #cfdcff;
    }

    &.ant-radio-wrapper-checked {
      background: ${neutralSelected};
      box-shadow: inset 0 0 0 1px #bdd0ff;
    }

    &:hover .ant-radio + span {
      color: ${colors.black};
    }
  }
`

Quiz.ListRadioGroup = styled(Radio.Group)`
  && {
    width: 100%;
  }
`

Quiz.Checkbox = styled(Checkbox)`
  && {
    display: flex;
    align-items: flex-start;
    gap: 0.8rem;
    width: 100%;
    min-height: 3.2rem;
    font-size: 1.5rem;
    font-weight: 400;
    text-align: left;
    color: ${readableText};
    margin-bottom: 0.52rem;
    padding: 0.72rem 0.92rem;
    border: 0;
    border-radius: 10px;
    background: ${neutralCard};
    box-shadow: inset 0 0 0 1px #dfe8fb;
    transition: background-color 0.15s ease, box-shadow 0.15s ease;

    .ant-checkbox {
      top: 0.2rem;
    }

    .ant-checkbox + span {
      color: ${readableText};
      line-height: 1.5;
      padding-left: 0;
    }

    .ant-checkbox-checked + span {
      color: ${colors.black};
      font-weight: 600;
    }

    &:hover {
      background: ${neutralHover};
      box-shadow: inset 0 0 0 1px #cfdcff;
    }

    &.ant-checkbox-wrapper-checked {
      background: ${neutralSelected};
      box-shadow: inset 0 0 0 1px #bdd0ff;
    }

    &:hover .ant-checkbox + span {
      color: ${colors.black};
    }
  }
`

Quiz.ListCheckboxGroup = styled(Checkbox.Group)`
  && {
    width: 100%;
    display: flex;
    flex-direction: column;

    .ant-checkbox-wrapper + .ant-checkbox-wrapper {
      margin-left: 0;
    }
  }
`

Quiz.TextArea = styled(Input.TextArea)`
  && {
    color: ${readableText};
    font-size: 1.5rem;
    line-height: 1.5;
    border-radius: 10px;
    border: 0;
    background: #f7f8fa;
    box-shadow: inset 0 0 0 1px #e4e8ef;

    &:focus,
    &:hover {
      box-shadow: inset 0 0 0 1px #d6dde8;
    }
  }
`

Quiz.SortableList = styled.div`
  position: relative;
  text-align: justify;
`

Quiz.SortableItem = styled.div`
  width: 100%;
  background: #f7f8fa;
  border: 0;
  cursor: move;
  padding: 0.7rem 1rem;
  margin-bottom: 0.6rem;
  z-index: 99999;
  font-size: 1.5rem;
  color: ${readableText};
  border-radius: 10px;
  box-shadow: inset 0 0 0 1px #e4e8ef;
`

Quiz.ElementsList = styled.div`
  position: relative;
  text-align: justify;
  ${props =>
    props.hide &&
    css`
      display: none;
    `}
`

Quiz.Element = styled.div`
  background: #f7f8fa;
  border: 0;
  cursor: move;
  padding: 0.7rem 1rem;
  z-index: 99999;
  margin-bottom: 0.6rem;
  color: ${readableText};
  border-radius: 10px;
  box-shadow: inset 0 0 0 1px #e4e8ef;
`

Quiz.ElementDrop = styled.div`
  background: #f7f8fa;
  border: 0;
  cursor: move;
  padding: 0.7rem 1rem;
  z-index: 99999;
  color: ${readableText};
  border-radius: 10px;
  box-shadow: inset 0 0 0 1px #e4e8ef;
`

Quiz.CriterionList = styled.div`
  pointer-events: none;
  text-align: justify;
`

Quiz.CriterionContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-content: center;

  &:not(:first-child) {
    margin-top: 0.6rem;
  }
`

Quiz.Criterion = styled.div`
  border: 0;
  flex: 1;
  padding: 0.7rem 1rem;
  margin-right: 0.6rem;
  color: ${readableText};
  border-radius: 10px;
  background: #f7f8fa;
  box-shadow: inset 0 0 0 1px #e4e8ef;
`

Quiz.Area = styled.div`
  border: 0;
  flex: 1;
  padding: 0.6rem;
  border-radius: 10px;
  background: #f7f8fa;
  box-shadow: inset 0 0 0 1px #e4e8ef;
`

Quiz.Image = styled.img`
  width: 100%;
  max-width: calc(50% - 0.5rem);
  margin-top: 0.6rem;
`

Quiz.Result = styled.div`
  height: auto;
  overflow-y: auto;
`

Quiz.Result.Title = styled.div`
  width: 100%;
  color: ${colors.red};
  // font-size: 2.1rem;
  font-size: 2.8rem;
  margin-bottom: 2rem;
  text-align: center;

  ${props =>
    props.status &&
    css`
      color: ${colors.green};
    `}
`

Quiz.Result.Label = styled.div`
  border: 0.1rem solid ${colors.red};
  color: ${colors.red};
  padding: 0.5rem 0.5rem 0.5rem 3.5rem;
  margin: 0.5rem 0;
  position: relative;

  ${props =>
    props.status &&
    css`
      border-color: ${colors.green};
      color: ${colors.green};
    `}
`

Quiz.Result.Icon = styled(Icon)`
  position: absolute;
  top: 50%;
  left: 1rem;
  transform: translateY(-50%);
`

Quiz.Result.Load = styled.div`
  width: 100%;
  font-size: 1.8rem;
  padding: 3rem 0;
  text-align: center;
`

// Add to your styled.js file
Quiz.Result.Feedback = styled.div`
  margin-top: 10px;
  padding: 10px;
  background-color: #f0f8ff;
  border-left: 4px solid #1890ff;
  font-style: italic;
`

Quiz.Reset = styled(Button)`
  margin-bottom: 2rem;
`

Quiz.UnityContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`

Quiz.FullScreen = styled.div`
  position: absolute;
  right: 10px;
  bottom: 15px;
  padding: 1rem;
  background: #ebebeb;
  display: grid;
  place-items: center;
  z-index: 9;
`

export { Quiz }
